// client/src/components/AdminDashboard.js

import React, { useEffect, useState } from 'react';
import { Typography, Alert, Box, Button, Grid, Card, CardContent, CardActions } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import FlagIcon from '@mui/icons-material/Flag';
import BarChartIcon from '@mui/icons-material/BarChart';
import StorageIcon from '@mui/icons-material/Storage';
import ImageIcon from '@mui/icons-material/Image';
import EmailIcon from '@mui/icons-material/Email';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import HubIcon from '@mui/icons-material/Hub';
// [registry-strip-start]
import DownloadIcon from '@mui/icons-material/Download';
// [registry-strip-end]
// [seo-strip-start]
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
// [seo-strip-end]
import { useNavigate, Link } from 'react-router-dom';
import AppBarHeader from './AppBarHeader';
import { jwtDecode } from 'jwt-decode';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';

function AdminDashboard() {
  const navigate = useNavigate();
  const { featureFlags } = useFeatureFlags();
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;
  const userId = user ? user.id : null;
  const isAdmin = user ? user.isAdmin : false;
  // [seo-strip-start]
  const [seoProgress, setSeoProgress] = useState(null);
  // [seo-strip-end]
  // [registry-strip-start]
  const [distributionVersion, setDistributionVersion] = useState(null);
  // [registry-strip-end]

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // [seo-strip-start]
  useEffect(() => {
    if (!isAdmin) return;
    import('../axiosInstance').then(({ default: axiosInstance }) => {
      axiosInstance.get('/admin/seo-settings')
        .then(res => {
          const d = res.data;
          const completed = [
            d['enableDiscovery'],
            true,
            !!d['seo.checklist.googleSubmitted'],
            !!d['seo.checklist.ownershipVerified'],
            !!d['seo.checklist.sitemapSubmitted'],
          ].filter(Boolean).length;
          setSeoProgress(completed);
        })
        .catch(() => {});
    });
  }, [isAdmin]);
  // [seo-strip-end]

  // [registry-strip-start]
  useEffect(() => {
    if (!isAdmin) return;
    import('../axiosInstance').then(({ default: axiosInstance }) => {
      axiosInstance.get('/settings')
        .then(res => {
          if (res.data['distribution.currentVersion']) {
            setDistributionVersion(res.data['distribution.currentVersion']);
          }
        })
        .catch(() => {});
    });
  }, [isAdmin]);
  // [registry-strip-end]

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const adminSections = [
    {
      title: 'User Management',
      description: 'Manage users, roles, and permissions',
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: 'primary',
      path: '/admin/users'
    },
    {
      title: 'Moderation Dashboard',
      description: 'Review reports and moderate content',
      icon: <FlagIcon sx={{ fontSize: 40 }} />,
      color: 'error',
      path: '/admin/moderation'
    },
    {
      title: 'Feature Flags',
      description: 'Enable/disable platform features',
      icon: <SettingsIcon sx={{ fontSize: 40 }} />,
      color: 'warning',
      path: '/admin/feature-flags'
    },
    {
      title: 'Statistics',
      description: 'View platform analytics and metrics',
      icon: <BarChartIcon sx={{ fontSize: 40 }} />,
      color: 'info',
      path: '/admin/statistics'
    },
    {
      title: 'Database Management',
      description: 'Manage database operations',
      icon: <StorageIcon sx={{ fontSize: 40 }} />,
      color: 'success',
      path: '/admin/database'
    },
    {
      title: 'Image Processing',
      description: 'Manage shelf-ready image processing for collections',
      icon: <ImageIcon sx={{ fontSize: 40 }} />,
      color: 'secondary',
      path: '/admin/images'
    },
    {
      title: 'Email Settings',
      description: 'Configure SMTP mail delivery',
      icon: <EmailIcon sx={{ fontSize: 40 }} />,
      color: 'primary',
      path: '/admin/email-settings'
    },
    {
      title: 'Quota Harvesting',
      description: 'Manage API quota usage and local catalogue building',
      icon: <CloudDownloadIcon sx={{ fontSize: 40 }} />,
      color: 'secondary',
      path: '/admin/quota-harvesting',
      featureFlag: 'enableQuotaHarvesting'
    },
    {
      title: 'API Keys',
      description: 'Manage external API keys (RAWG, IGDB, OMDB, eBay and more)',
      icon: <VpnKeyIcon sx={{ fontSize: 40 }} />,
      color: 'warning',
      path: '/admin/api-keys'
    },
    {
      title: 'Federation Settings',
      description: 'Configure ActivityPub federation with other InterCollect instances',
      icon: <HubIcon sx={{ fontSize: 40 }} />,
      color: 'primary',
      path: '/admin/federation-settings',
      featureFlag: 'enableFederation'
    },
    // [seo-strip-start]
    {
      title: 'SEO & Discovery',
      description: seoProgress !== null
        ? `${seoProgress} / 5 steps complete`
        : 'Configure search engine visibility and About page',
      icon: <TravelExploreIcon sx={{ fontSize: 40 }} />,
      color: 'success',
      path: '/admin/seo',
      featureFlag: 'enableDiscovery'
    },
    // [seo-strip-end]
    // [registry-strip-start]
    {
      title: 'Distribution',
      description: distributionVersion ? `Published: ${distributionVersion}` : 'Not yet published',
      icon: <DownloadIcon sx={{ fontSize: 40 }} />,
      color: distributionVersion ? 'success' : 'inherit',
      path: '/download',
      featureFlag: 'enableDistribution',
    },
    // [registry-strip-end]
  ].filter(s => !s.featureFlag || featureFlags[s.featureFlag]);

  if (!isAdmin) {
    return (
      <>
        <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Access denied. Admin privileges required.</Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} />
      <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Admin Dashboard
        </Typography>
        
        <Grid container spacing={3}>
          {adminSections.map((section) => (
            <Grid item xs={12} sm={6} md={4} key={section.path}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                  <Box sx={{ color: `${section.color}.main`, mb: 2 }}>
                    {section.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {section.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {section.description}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                  <Button
                    variant="contained"
                    color={section.color}
                    onClick={() => navigate(section.path)}
                    fullWidth
                    sx={{ mx: 2 }}
                  >
                    Open
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  );
}

export default AdminDashboard;